import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '../../../core/config/app-config.service';
import { map, catchError } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';

export interface CreateFormSubmissionPayload {
  formId: string;
  description?: string;
  formData: Record<string, any>;
  status?: string;
  fromStep?: number | null;
  toStep?: number | null;
}

export interface TriggerActionPayload {
  formData: Record<string, any>;
  /** Guardar el FormData resultante. Por defecto una simulacion no persiste. */
  persist?: boolean;
  description?: string;
}

export interface CreateAndLinkFormSubmissionPayload {
  formId: string;
  description: string;
  entityTypeId: number;
  entityId: string;
  formData?: Record<string, any>;
}

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  private _idKatios: string = '';
  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private router: Router,
  ) {}

  set idKatios(val: string) {
    this._idKatios = val;
  }

  private extraerNombreArchivo(url: string): string {
    const partes = url.split('/');
    return partes[partes.length - 1] ?? 'documento.pdf';
  }

  getSecOpcionesXPerfil(idForma: string, perfil: string) {
    if (!this._idKatios) {
      return Promise.reject(new Error('idKatios not set'));
    }
    let params = new HttpParams();
    params = params
      .set('idAplicacion', 'FORMULARIOS')
      .set('idForma', idForma)
      .set('Perfil', perfil);
    return this.http
      .get(
        `${this.appConfigService.ServidorGenerales}/General/OpcionesXPerfil/${this._idKatios}`,
        { params },
      )
      .toPromise()
      .then((res: any) => {
        if (res) {
          res.forEach((opc: any) => {
            try {
              opc.valor = JSON.parse(opc.valor);
            } catch (error) {}
          });
          return res;
        } else throw new Error();
      });
  }

  getCrudByEntity(idKatios: string, idEntidad: string) {
    return this.http
      .get<any>(
        `${this.appConfigService.ServicioForms}/forms/${idKatios}/${idEntidad}`,
      )
      .pipe(map((response) => response.data));
  }

  getProductSettings(idKatios: string): Observable<any> {
    return this.http.get(
      `${this.appConfigService.ServerScUtilities}/katios-settings/${idKatios}`,
    );
  }

  createFormSubmission(
    idKatios: string,
    payload: CreateFormSubmissionPayload,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.appConfigService.ServicioForms}/forms-submission/${idKatios}`,
      payload,
    );
  }

  updateFormSubmission(
    idKatios: string,
    submissionId: number,
    payload: CreateFormSubmissionPayload,
  ): Observable<any> {
    return this.http.put<any>(
      `${this.appConfigService.ServicioForms}/forms-submission/${idKatios}/${submissionId}`,
      payload,
    );
  }

  /**
   * Accion bajo demanda (ej. "simular"): ejecuta las acciones con ese Trigger en FormService
   * sin cambiar de step y devuelve el FormData con los fieldsMatches aplicados.
   */
  executeTriggerAction(
    idKatios: string,
    submissionId: number,
    trigger: string,
    payload: TriggerActionPayload,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.appConfigService.ServicioForms}/forms-submission/${idKatios}/${submissionId}/actions/${encodeURIComponent(trigger)}`,
      payload,
    );
  }

  getFirstFormId(idKatios: string): Observable<string | null> {
    return this.http
      .get<any>(`${this.appConfigService.ServicioForms}/forms/${idKatios}`)
      .pipe(
        map((response) => response?.data?.formId ?? null),
        catchError(() => of(null)),
      );
  }

  getFormsByNdoc(idKatios: string, ndoc: string, formId?: string, entityTypeId?: number): Observable<any[]> {
    let params = new HttpParams().set('entityId', ndoc);
    if (formId) params = params.set('formId', formId);
    if (entityTypeId != null) params = params.set('entityTypeId', entityTypeId.toString());
    return this.http
      .get<any>(
        `${this.appConfigService.ServicioForms}/forms-submission/${idKatios}/by-entity-id`,
        { params },
      )
      .pipe(map((response) => response.data));
  }

  createAndLinkFormSubmission(
    idKatios: string,
    payload: CreateAndLinkFormSubmissionPayload,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.appConfigService.ServicioForms}/forms-submission/${idKatios}/create-and-link`,
      payload,
    );
  }

  getSubmissionWithBlob(
    idKatios: string,
    submissionId: number,
    currentStep: number,
  ): Observable<{ formData: any; resolvedStep: number }> {
    const resolvedStep = Math.max(0, currentStep - 1);
    const url = `${this.appConfigService.ServicioForms}/forms-submission/${idKatios}/${submissionId}`;
    return this.http.get<any>(url).pipe(
      switchMap((response: any) => {
        const path: string = response?.data?.path ?? '';
        if (!path) return of({ formData: null, resolvedStep });
        const relative = path.replace(/^temp\//, '');
        const blobUrl = this.appConfigService.BlobUrl + relative;
        return this.http.get<any>(blobUrl).pipe(
          map((formData: any) => ({ formData, resolvedStep })),
          catchError(() => of({ formData: null, resolvedStep })),
        );
      }),
      catchError(() => of({ formData: null, resolvedStep })),
    );
  }

  fillFormStepFromVoice(idKatios: string, payload: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.appConfigService.ServicioForms}/voice-fill/${idKatios}`,
      payload,
    );
  }

  getPeopleTFA(ndoc: string): Observable<{ celular: string; email: string } | null> {
    const base = this.appConfigService.ServidorPeople.replace(/\/+$/, '');
    const segment = this._idKatios.startsWith('TFAK') ? 'TFA' : this._idKatios;
    return this.http.get<any>(`${base}/${segment}/${ndoc}`).pipe(
      map(res => ({
        celular: res?.data?.Celular ?? '',
        email: res?.data?.Email ?? '',
      })),
      catchError(() => of(null)),
    );
  }

  sendTfaOtp(username: string, channel: 'sms' | 'mail'): Observable<any> {
    const base = this.appConfigService.ServidorAuth.replace(/\/+$/, '');
    const params = new HttpParams().set('username', username).set('channel', channel);
    return this.http.post(`${base}/Login/sendOTP/TFA`, null, { params });
  }

  verifyTfaOtp(username: string, otp: string): Observable<boolean> {
    const base = this.appConfigService.ServidorAuth.replace(/\/+$/, '');
    const params = new HttpParams().set('username', username).set('otp', otp);
    return this.http.get<any>(`${base}/Login/loginOTP/TFA`, { params }).pipe(
      map(res => res?.success === true || res?.success === false || res?.errors === null || !!res?.data?.token),
    );
  }

  sendSimpleOtp(idKatios: string, username: string, channel: 'sms' | 'mail'): Observable<any> {
    const base = this.appConfigService.ServidorAuth.replace(/\/+$/, '');
    const params = new HttpParams().set('username', username).set('channel', channel);
    return this.http.post(`${base}/Login/sendSimpleOTP/${idKatios}`, null, { params });
  }

  validateSimpleOtp(idKatios: string, username: string, otp: string): Observable<boolean> {
    const base = this.appConfigService.ServidorAuth.replace(/\/+$/, '');
    const params = new HttpParams().set('username', username).set('otp', otp);
    return this.http.get<any>(`${base}/Login/validateSimpleOTP/${idKatios}`, { params }).pipe(
      map(res => res?.data?.valid === true),
    );
  }
}
